import { Controller } from "@hotwired/stimulus"
import cytoscape from "cytoscape"

export default class extends Controller {
  static targets = ["content", "visualization"]
  
  connect() {
    this.clusterLoaded = false
    this.identityColors = [
      '#dc3545', // red
      '#fd7e14', // orange  
      '#ffc107', // yellow
      '#198754', // green
      '#0dcaf0', // cyan
      '#0d6efd', // blue
      '#6610f2', // indigo
      '#6f42c1', // purple
      '#d63384', // pink
    ]
    
    // Check if turbo frame has already loaded
    const frameLoaded = this.element.querySelector('.document-app__cluster-data')
    if (frameLoaded) {
      this.loadClusterData()
    }
    
    // Also observe for turbo frame loads
    this.observer = new MutationObserver(() => {
      const dataElement = this.element.querySelector('.document-app__cluster-data')
      if (dataElement && !this.visualizationRendered) {
        this.loadClusterData()
      }
    })
    
    this.observer.observe(this.contentTarget, { childList: true, subtree: true })
  }
  
  disconnect() {
    if (this.observer) {
      this.observer.disconnect()
    }
    
    // Clean up fullscreen event listeners
    if (this.fullscreenElements && this.fullscreenElements.handler) {
      document.removeEventListener('fullscreenchange', this.fullscreenElements.handler)
      document.removeEventListener('webkitfullscreenchange', this.fullscreenElements.handler)
      document.removeEventListener('mozfullscreenchange', this.fullscreenElements.handler)
      document.removeEventListener('MSFullscreenChange', this.fullscreenElements.handler)
    }
    
    // Exit fullscreen if active
    if (document.fullscreenElement) {
      document.exitFullscreen()
    }
  }

  loadCluster() {
    // Only load once
    if (!this.clusterLoaded) {
      this.clusterLoaded = true
      // Try to load data after frame loads
      setTimeout(() => this.loadClusterData(), 100)
    }
  }
  
  loadClusterData() {
    const dataElement = this.element.querySelector('.document-app__cluster-data')
    if (dataElement && !this.visualizationRendered) {
      const clusterData = JSON.parse(dataElement.dataset.cluster)
      const currentToken = dataElement.dataset.currentToken
      const accessibleTokens = JSON.parse(dataElement.dataset.accessibleTokens || '[]')
      this.renderVisualization({
        detail: { cluster: clusterData, currentToken: currentToken, accessibleTokens: accessibleTokens }
      })
      this.visualizationRendered = true
    }
  }

  renderVisualization(event) {
    const data = event.detail.cluster
    const accessibleTokens = event.detail.accessibleTokens || []
    if (!data || !this.hasVisualizationTarget) return

    // Clear previous visualization
    this.visualizationTarget.innerHTML = ""
    
    // Check if in dark mode
    const isDarkMode = document.documentElement.getAttribute('data-bs-theme') === 'dark'
    
    // Create wrapper for fullscreen
    const wrapper = document.createElement('div')
    wrapper.className = 'cluster-visualization-wrapper'
    wrapper.style.position = 'relative'
    
    // Create container with proper height
    const container = document.createElement('div')
    container.id = 'cy'
    container.style.width = '100%'
    container.style.height = '500px'
    container.style.backgroundColor = isDarkMode ? '#1E1E1E' : '#f8f9fa'
    container.style.borderRadius = '0.5rem'
    
    // Add fullscreen button
    const fullscreenBtn = document.createElement('button')
    fullscreenBtn.className = 'btn btn-sm btn-outline-secondary'
    fullscreenBtn.style.position = 'absolute'
    fullscreenBtn.style.top = '10px'
    fullscreenBtn.style.left = '10px'
    fullscreenBtn.style.zIndex = '1000'
    fullscreenBtn.innerHTML = '<i class="fa-solid fa-expand"></i>'
    fullscreenBtn.title = 'Toggle fullscreen'
    
    fullscreenBtn.addEventListener('click', () => {
      this.toggleFullscreen(wrapper, container, fullscreenBtn)
    })
    
    // Listen for fullscreen changes (including ESC key)
    const fullscreenChangeHandler = () => {
      if (!document.fullscreenElement) {
        // Fullscreen was exited (by ESC or browser controls)
        this.setFullscreenStyles(container, fullscreenBtn, false)
      }
    }
    
    // Add event listeners for different browsers
    document.addEventListener('fullscreenchange', fullscreenChangeHandler)
    document.addEventListener('webkitfullscreenchange', fullscreenChangeHandler)
    document.addEventListener('mozfullscreenchange', fullscreenChangeHandler)
    document.addEventListener('MSFullscreenChange', fullscreenChangeHandler)
    
    // Store references for cleanup
    this.fullscreenElements = { wrapper, container, button: fullscreenBtn, handler: fullscreenChangeHandler }
    
    wrapper.appendChild(fullscreenBtn)
    wrapper.appendChild(container)
    this.visualizationTarget.appendChild(wrapper)

    // Prepare nodes and edges for Cytoscape
    const elements = []
    const currentToken = event.detail.currentToken
    
    // Build a map of documents by identity for easy lookup
    const documentsByIdentity = {}
    const identityAttributes = {}
    
    data.cluster.identities.forEach((identity, index) => {
      const identityId = `identity-${index}`
      documentsByIdentity[identityId] = identity.document_tokens
      
      // Collect all unique attributes for this identity
      identityAttributes[identityId] = {
        emails: new Set(),
        phones: new Set(),
        addresses: new Set()
      }
      
      // Gather attributes from all documents of this identity
      identity.document_tokens.forEach(token => {
        const doc = data.cluster_documents.find(d => d.document_token === token)
        if (doc) {
          if (doc.attributes.email) identityAttributes[identityId].emails.add(doc.attributes.email)
          if (doc.attributes.phone) identityAttributes[identityId].phones.add(doc.attributes.phone)
          if (doc.attributes.address_full) identityAttributes[identityId].addresses.add(doc.attributes.address_full)
        }
      })
    })

    // Create identity nodes
    data.cluster.identities.forEach((identity, index) => {
      const identityId = `identity-${index}`
      const color = this.identityColors[index % this.identityColors.length]
      const hasCurrentDocument = identity.document_tokens.includes(currentToken)
      
      // Check if user has access to any of this identity's documents
      const hasAccess = identity.document_tokens.some(token => accessibleTokens.includes(token))
      
      // Calculate node size based on document count with logarithmic scaling
      // This provides better visual balance for clusters with varying document counts
      const minNodeSize = 60
      const maxNodeSize = 150
      
      // Use logarithmic scaling for better visual distribution
      // log(1) = 0, so we add 1 to avoid zero for single documents
      const logScale = Math.log(identity.document_count + 1) / Math.log(10)
      
      // Map logarithmic scale to our size range
      const sizeRange = maxNodeSize - minNodeSize
      const nodeSize = minNodeSize + (logScale * sizeRange * 0.5)
      
      // Use generic label if no access
      const displayName = hasAccess ? identity.primary_name : `Identity ${index + 1}`
      
      elements.push({
        data: { 
          id: identityId,
          label: `${displayName}\n(${identity.document_count} docs)`,
          name: displayName,
          realName: identity.primary_name,
          documentCount: identity.document_count,
          documentTokens: identity.document_tokens,
          color: color,
          nodeSize: nodeSize,
          hasCurrentDocument: hasCurrentDocument,
          hasAccess: hasAccess
        },
        classes: hasCurrentDocument ? 'current-identity' : ''
      })
    })

    // Create edges between identities based on shared attributes
    const processedPairs = new Set()
    
    data.cluster.identities.forEach((identity1, i) => {
      data.cluster.identities.forEach((identity2, j) => {
        if (i >= j) return
        
        const id1 = `identity-${i}`
        const id2 = `identity-${j}`
        const pairKey = [id1, id2].sort().join('-')
        
        if (processedPairs.has(pairKey)) return
        processedPairs.add(pairKey)

        // Check if both identities are accessible
        const hasAccess1 = identity1.document_tokens.some(token => accessibleTokens.includes(token))
        const hasAccess2 = identity2.document_tokens.some(token => accessibleTokens.includes(token))
        const bothAccessible = hasAccess1 && hasAccess2

        const sharedAttrs = []
        const sharedAttrTypes = []
        let edgeWidth = 2
        
        // Check for shared emails
        const sharedEmails = [...identityAttributes[id1].emails].filter(e => 
          identityAttributes[id2].emails.has(e)
        )
        if (sharedEmails.length > 0) {
          if (bothAccessible) {
            sharedAttrs.push(`Email: ${sharedEmails.join(', ')}`)
          } else {
            sharedAttrs.push('Email')
          }
          sharedAttrTypes.push('email')
          edgeWidth += 2
        }
        
        // Check for shared phones
        const sharedPhones = [...identityAttributes[id1].phones].filter(p => 
          identityAttributes[id2].phones.has(p)
        )
        if (sharedPhones.length > 0) {
          if (bothAccessible) {
            sharedAttrs.push(`Phone: ${sharedPhones.join(', ')}`)
          } else {
            sharedAttrs.push('Phone')
          }
          sharedAttrTypes.push('phone')
          edgeWidth += 2
        }
        
        // Check for shared addresses
        const sharedAddresses = [...identityAttributes[id1].addresses].filter(a => 
          identityAttributes[id2].addresses.has(a)
        )
        if (sharedAddresses.length > 0) {
          if (bothAccessible) {
            sharedAttrs.push(`Address: ${sharedAddresses[0].substring(0, 30)}...`)
          } else {
            sharedAttrs.push('Address')
          }
          sharedAttrTypes.push('address')
          edgeWidth += 2
        }

        if (sharedAttrs.length > 0) {
          elements.push({
            data: {
              id: pairKey,
              source: id1,
              target: id2,
              label: sharedAttrs.join('\n'),
              sharedAttributes: sharedAttrs,
              sharedAttributeTypes: sharedAttrTypes,
              width: edgeWidth,
              color: sharedAttrs.length > 1 ? '#dc3545' : '#6c757d', // Red for multiple shared attrs
              bothAccessible: bothAccessible
            }
          })
        }
      })
    })

    // Initialize Cytoscape
    this.cy = cytoscape({
      container: container,
      elements: elements,
      style: [
        {
          selector: 'node',
          style: {
            'background-color': 'data(color)',
            'label': 'data(label)',
            'text-valign': 'center',
            'text-halign': 'center',
            'text-wrap': 'wrap',
            'text-max-width': '120px',
            'font-size': '12px',
            'width': 'data(nodeSize)',
            'height': 'data(nodeSize)',
            'border-width': '3px',
            'border-color': isDarkMode ? '#2C2C2C' : '#fff',
            'color': isDarkMode ? '#000' : '#fff',
            'font-weight': 'bold',
            'text-outline-width': isDarkMode ? '0' : '2px',
            'text-outline-color': 'data(color)',
            'text-background-color': isDarkMode ? '#fff' : 'transparent',
            'text-background-opacity': isDarkMode ? 0.9 : 0,
            'text-background-padding': isDarkMode ? '2px' : '0'
          }
        },
        {
          selector: 'node.current-identity',
          style: {
            'border-width': '5px',
            'border-color': '#0d6efd',
            'font-size': '14px',
            'z-index': 999
          }
        },
        {
          selector: 'edge',
          style: {
            'width': 'data(width)',
            'line-color': 'data(color)',
            'target-arrow-color': 'data(color)',
            'target-arrow-shape': 'triangle',
            'curve-style': 'bezier',
            'label': 'data(label)',
            'font-size': '10px',
            'text-background-color': isDarkMode ? '#2C2C2C' : '#fff',
            'text-background-opacity': 0.9,
            'text-background-padding': '3px',
            'text-wrap': 'wrap',
            'text-max-width': '150px',
            'color': isDarkMode ? '#e9ecef' : '#212529',
            'text-background-shape': 'roundrectangle'
          }
        },
        {
          selector: 'node:selected',
          style: {
            'border-width': '5px',
            'border-color': '#ffc107',
            'overlay-color': '#ffc107',
            'overlay-opacity': 0.2
          }
        }
      ],
      layout: {
        name: 'cose',
        animate: true,
        animationDuration: 1000,
        nodeRepulsion: 8000,
        idealEdgeLength: 150,
        edgeElasticity: 100,
        nestingFactor: 5,
        gravity: 80,
        numIter: 1000,
        initialTemp: 200,
        coolingFactor: 0.95,
        minTemp: 1.0,
        padding: 50
      },
      wheelSensitivity: 0.2,
      minZoom: 0.3,
      maxZoom: 3
    })
    
    // After layout is complete, fit with padding
    this.cy.on('layoutstop', () => {
      this.cy.fit(50) // 50px padding around the graph
    })

    // Add hover effects
    this.cy.on('mouseover', 'node', (evt) => {
      const node = evt.target
      const currentSize = node.data('nodeSize')
      // Scale hover effect proportionally (20% increase)
      const hoverSize = currentSize * 1.2
      node.style('width', hoverSize)
      node.style('height', hoverSize)
      node.style('font-size', '14px')
      node.style('z-index', 1000)
      
      // Highlight connected edges with more width
      node.connectedEdges().forEach(edge => {
        const currentWidth = edge.data('width')
        edge.style('width', currentWidth + 4)
      })
    })

    this.cy.on('mouseout', 'node', (evt) => {
      const node = evt.target
      const originalSize = node.data('nodeSize')
      node.style('width', originalSize)
      node.style('height', originalSize)
      node.style('font-size', node.hasClass('current-identity') ? '14px' : '12px')
      node.style('z-index', 1)
      
      // Reset edge width
      node.connectedEdges().forEach(edge => {
        const originalWidth = edge.data('width')
        edge.style('width', originalWidth)
      })
    })

    // Add click handler for nodes
    this.cy.on('tap', 'node', (evt) => {
      const node = evt.target
      const data = node.data()
      
      // Create and show tooltip
      const tooltip = document.createElement('div')
      tooltip.className = 'cytoscape-tooltip'
      
      // Gather all unique attributes for this identity
      const attrs = identityAttributes[data.id]
      
      let tooltipContent = `<strong>${data.name}</strong><br>`
      tooltipContent += `<small>${data.documentCount} document${data.documentCount > 1 ? 's' : ''}</small><br>`
      
      if (data.hasAccess) {
        // Show full details for accessible identities
        const emails = attrs ? [...attrs.emails].join(', ') : ''
        const phones = attrs ? [...attrs.phones].join(', ') : ''
        const addresses = attrs ? [...attrs.addresses].map(a => a.substring(0, 50) + '...').join('<br>') : ''
        
        tooltipContent += `<small>Tokens: ${data.documentTokens.map(t => t.substring(0, 8) + '...').join(', ')}</small><br>`
        if (emails) tooltipContent += `<small>Email${attrs.emails.size > 1 ? 's' : ''}: ${emails}</small><br>`
        if (phones) tooltipContent += `<small>Phone${attrs.phones.size > 1 ? 's' : ''}: ${phones}</small><br>`
        if (addresses) tooltipContent += `<small>Address${attrs.addresses.size > 1 ? 'es' : ''}: ${addresses}</small>`
      } else {
        // Show limited info for inaccessible identities
        const attrTypes = []
        if (attrs && attrs.emails.size > 0) attrTypes.push(`${attrs.emails.size} email${attrs.emails.size > 1 ? 's' : ''}`)
        if (attrs && attrs.phones.size > 0) attrTypes.push(`${attrs.phones.size} phone${attrs.phones.size > 1 ? 's' : ''}`)
        if (attrs && attrs.addresses.size > 0) attrTypes.push(`${attrs.addresses.size} address${attrs.addresses.size > 1 ? 'es' : ''}`)
        
        tooltipContent += `<small style="color: #888;">No access to identity data</small><br>`
        if (attrTypes.length > 0) {
          tooltipContent += `<small>Has: ${attrTypes.join(', ')}</small>`
        }
      }
      
      tooltip.innerHTML = tooltipContent
      
      // Position tooltip
      const renderedPosition = node.renderedPosition()
      tooltip.style.position = 'absolute'
      tooltip.style.left = `${renderedPosition.x}px`
      tooltip.style.top = `${renderedPosition.y - 150}px`
      tooltip.style.backgroundColor = isDarkMode ? 'rgba(44, 44, 44, 0.95)' : 'rgba(0, 0, 0, 0.9)'
      tooltip.style.color = isDarkMode ? '#e9ecef' : 'white'
      tooltip.style.padding = '10px'
      tooltip.style.borderRadius = '4px'
      tooltip.style.fontSize = '11px'
      tooltip.style.zIndex = '1001'
      tooltip.style.maxWidth = '300px'
      tooltip.style.border = isDarkMode ? '1px solid #495057' : 'none'
      
      container.appendChild(tooltip)
      
      // Remove tooltip after 5 seconds or on next click
      setTimeout(() => tooltip.remove(), 5000)
      this.cy.one('tap', () => tooltip.remove())
    })

    // Add legend
    this.addLegend(container, data.cluster.identities, identityAttributes, isDarkMode, accessibleTokens)
  }
  
  toggleFullscreen(wrapper, container, button) {
    if (!document.fullscreenElement) {
      // Enter fullscreen
      wrapper.requestFullscreen().then(() => {
        this.setFullscreenStyles(container, button, true)
      })
    } else {
      // Exit fullscreen
      document.exitFullscreen()
    }
  }
  
  setFullscreenStyles(container, button, isFullscreen) {
    if (isFullscreen) {
      container.style.height = '100vh'
      container.style.width = '100vw'
      container.style.position = 'fixed'
      container.style.top = '0'
      container.style.left = '0'
      container.style.zIndex = '9999'
      container.style.borderRadius = '0'
      
      button.innerHTML = '<i class="fa-solid fa-compress"></i>'
      button.style.position = 'fixed'
    } else {
      container.style.height = '500px'
      container.style.width = '100%'
      container.style.position = 'relative'
      container.style.top = 'auto'
      container.style.left = 'auto'
      container.style.zIndex = 'auto'
      container.style.borderRadius = '0.5rem'
      
      button.innerHTML = '<i class="fa-solid fa-expand"></i>'
      button.style.position = 'absolute'
    }
    
    // Resize cytoscape to fit new dimensions
    setTimeout(() => {
      if (this.cy) {
        this.cy.resize()
        this.cy.fit(50)
      }
    }, 100)
  }

  addLegend(container, identities, identityAttributes, isDarkMode, accessibleTokens) {
    const legend = document.createElement('div')
    legend.className = 'cluster-legend'
    legend.style.position = 'absolute'
    legend.style.top = '10px'
    legend.style.right = '10px'
    legend.style.backgroundColor = isDarkMode ? 'rgba(44, 44, 44, 0.95)' : 'rgba(255, 255, 255, 0.9)'
    legend.style.padding = '10px'
    legend.style.borderRadius = '4px'
    legend.style.fontSize = '12px'
    legend.style.boxShadow = isDarkMode ? '0 2px 4px rgba(0,0,0,0.3)' : '0 2px 4px rgba(0,0,0,0.1)'
    legend.style.color = isDarkMode ? '#e9ecef' : '#212529'
    legend.style.border = isDarkMode ? '1px solid #495057' : 'none'
    
    legend.innerHTML = `<strong style="color: ${isDarkMode ? '#e9ecef' : '#212529'}">Identities:</strong><br>`
    
    identities.forEach((identity, index) => {
      const color = this.identityColors[index % this.identityColors.length]
      const hasAccess = identity.document_tokens.some(token => accessibleTokens.includes(token))
      const displayName = hasAccess ? identity.primary_name : `Identity ${index + 1}`
      
      legend.innerHTML += `
        <div style="margin: 4px 0;">
          <span style="display: inline-block; width: 12px; height: 12px; background-color: ${color}; border-radius: 2px; margin-right: 5px;"></span>
          ${displayName} (${identity.document_count} docs)
        </div>
      `
    })
    
    legend.innerHTML += `<br><strong style="color: ${isDarkMode ? '#e9ecef' : '#212529'}">Connection Risk:</strong><br>`
    legend.innerHTML += `
      <div style="margin: 4px 0;">
        <span style="display: inline-block; width: 20px; height: 4px; background-color: #dc3545; margin-right: 5px;"></span>
        Multiple shared attributes
      </div>
      <div style="margin: 4px 0;">
        <span style="display: inline-block; width: 20px; height: 2px; background-color: #6c757d; margin-right: 5px;"></span>
        Single shared attribute
      </div>
    `
    
    container.appendChild(legend)
  }
}